import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';
import { fetchActivePlans, formatStorage } from '../../services/plansService';

const {
  FiDollarSign, FiMessageSquare, FiDownload, FiHardDrive, FiRefreshCw,
  FiAlertTriangle, FiCheck, FiInfo, FiLock, FiZap,
  FiSettings, FiEdit3, FiX, FiUsers, FiVideo,
  FiFileText, FiCpu, FiShoppingBag, FiHeart,
  FiExternalLink, FiPackage, FiStar,
} = FiIcons;

const TABLE = 'monetization_config_1776389352234';

const CATEGORIES = [
  { id: 'donations',     label: 'Donations',          icon: FiHeart,        color: 'text-pink-600',    bg: 'bg-pink-50',    border: 'border-pink-200',    desc: 'Platform commission, donor tips, and featured campaign settings' },
  { id: 'docuvault',     label: 'Storage',            icon: FiHardDrive,    color: 'text-indigo-600',  bg: 'bg-indigo-50',  border: 'border-indigo-200',  desc: 'DocuVault storage limits — sourced live from Subscription Plans' },
  { id: 'templates',     label: 'Templates',          icon: FiFileText,     color: 'text-pink-600',    bg: 'bg-pink-50',    border: 'border-pink-200',    desc: 'Memorial template access and usage limits' },
  { id: 'ai_features',   label: 'AI Assistance',      icon: FiCpu,          color: 'text-yellow-600',  bg: 'bg-yellow-50',  border: 'border-yellow-200',  desc: 'AI-powered features and request quotas' },
  { id: 'concierge',     label: 'Concierge',          icon: FiUsers,        color: 'text-orange-600',  bg: 'bg-orange-50',  border: 'border-orange-200',  desc: 'Concierge and appointment booking limits' },
  { id: 'marketplace',   label: 'Merchant Boost',     icon: FiShoppingBag,  color: 'text-teal-600',    bg: 'bg-teal-50',    border: 'border-teal-200',    desc: 'Marketplace listings and commission settings' },
  { id: 'webinars',      label: 'Webinar Access',     icon: FiVideo,        color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-200',     desc: 'Webinar registration and access controls' },
  { id: 'communication', label: 'Communication',      icon: FiMessageSquare,color: 'text-blue-600',    bg: 'bg-blue-50',    border: 'border-blue-200',    desc: 'Family tree chat initiation limits' },
  { id: 'export',        label: 'Export & Downloads', icon: FiDownload,     color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', desc: 'Print, PDF, and download usage limits' },
  { id: 'messaging',     label: 'User Messaging',     icon: FiSettings,     color: 'text-purple-600',  bg: 'bg-purple-50',  border: 'border-purple-200',  desc: 'Upgrade prompts and user-facing copy' },
];

const PLAN_PALETTE = [
  { color: 'text-gray-600',   bg: 'bg-gray-50',   border: 'border-gray-200',   dot: 'bg-gray-400'   },
  { color: 'text-blue-600',   bg: 'bg-blue-50',   border: 'border-blue-200',   dot: 'bg-blue-500'   },
  { color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200',  dot: 'bg-amber-500'  },
  { color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200', dot: 'bg-purple-500' },
];

const StoragePlanCard = ({ plan, palette, index }) => {
  const isHighlight = plan.highlight;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      className={`relative bg-white rounded-2xl border-2 p-5 flex flex-col gap-3 transition-all
        ${isHighlight ? 'border-accent-gold shadow-lg shadow-accent-gold/10' : `${palette.border}`}`}
    >
      {isHighlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="flex items-center gap-1 px-3 py-1 bg-accent-gold text-accent-navy text-[9px] font-black uppercase tracking-widest rounded-full shadow">
            <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Popular
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${palette.dot}`} />
          <p className="text-sm font-black text-accent-navy capitalize">{plan.name}</p>
        </div>
        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border uppercase tracking-widest ${plan.is_active ? `${palette.bg} ${palette.color} ${palette.border}` : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
          {plan.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <p className={`text-2xl font-serif font-bold ${palette.color}`}>{formatStorage(plan.storage_mb)}</p>
          <p className="text-[10px] text-text-muted font-mono">{plan.storage_mb.toLocaleString()} MB</p>
        </div>
        <div className="w-full h-1.5 bg-brand-dark rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, (plan.storage_mb / 20480) * 100)}%` }}
            transition={{ duration: 0.7, delay: index * 0.06 + 0.2 }}
            className={`h-full rounded-full ${palette.dot}`}
          />
        </div>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-brand-dark">
        <p className="text-[10px] text-text-muted uppercase tracking-widest font-bold">Price</p>
        <p className="text-sm font-black text-accent-navy">
          {plan.price_usd === 0
            ? <span className="text-green-600">Free</span>
            : `$${plan.price_usd.toFixed(2)} / ${plan.billing_cycle}`}
        </p>
      </div>
    </motion.div>
  );
};

const StoragePanel = ({ plans, loading, error, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-10 h-10 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
        <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading plans…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-14 h-14 bg-red-50 border border-red-200 rounded-2xl flex items-center justify-center">
          <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-500" />
        </div>
        <div>
          <p className="text-sm font-bold text-red-700 mb-1">Failed to load plans</p>
          <p className="text-xs text-text-muted">{error}</p>
        </div>
        <button onClick={onRefresh}
          className="px-4 py-2 bg-accent-navy text-white text-xs font-bold rounded-xl hover:bg-accent-navy/90 transition-colors flex items-center gap-2">
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Retry
        </button>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-14 h-14 bg-brand-light border border-brand-dark rounded-2xl flex items-center justify-center">
          <SafeIcon icon={FiPackage} className="w-6 h-6 text-text-muted/40" />
        </div>
        <p className="text-sm text-text-muted">No active subscription plans found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-2xl">
        <SafeIcon icon={FiInfo} className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-black text-indigo-800 mb-0.5">Read-only — sourced from Subscription Plans API</p>
          <p className="text-[11px] text-indigo-600 leading-relaxed">
            Storage limits are defined per subscription tier. To modify them, update the plan directly in{' '}
            <strong>Pricing Settings</strong>. Changes reflect here immediately on refresh.
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="p-1.5 bg-indigo-100 hover:bg-indigo-200 rounded-lg transition-colors flex-shrink-0"
          title="Refresh from Plans API"
        >
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 text-indigo-600" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {plans.map((plan, idx) => (
          <StoragePlanCard
            key={plan.id}
            plan={plan}
            palette={PLAN_PALETTE[idx % PLAN_PALETTE.length]}
            index={idx}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 pt-2">
        {[
          {
            label: 'Free Tier',
            value: formatStorage(plans.find(p => p.price_usd === 0)?.storage_mb ?? 0),
            sub: 'No cost storage',
            color: 'text-green-600',
            bg: 'bg-green-50',
            border: 'border-green-200',
          },
          {
            label: 'Max Tier',
            value: formatStorage(Math.max(...plans.map(p => p.storage_mb))),
            sub: 'Highest plan limit',
            color: 'text-purple-600',
            bg: 'bg-purple-50',
            border: 'border-purple-200',
          },
          {
            label: 'Active Plans',
            value: plans.filter(p => p.is_active).length,
            sub: 'Visible to users',
            color: 'text-accent-navy',
            bg: 'bg-brand-light',
            border: 'border-brand-dark',
          },
        ].map(s => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-4 text-center`}>
            <p className={`text-xl font-serif font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mt-0.5">{s.label}</p>
            <p className="text-[9px] text-text-muted">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between p-4 bg-accent-navy/5 border border-accent-navy/15 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-accent-navy rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiPackage} className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-[11px] font-black text-accent-navy">Want to change storage limits?</p>
            <p className="text-[10px] text-text-muted">Edit plans in Pricing Settings → storage_mb field.</p>
          </div>
        </div>
        <a
          href="#pricing"
          className="flex items-center gap-1.5 px-4 py-2 bg-accent-navy text-white text-[11px] font-black rounded-xl hover:bg-accent-navy/90 transition-colors"
        >
          Pricing Settings <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

const ConfigRow = ({ item, onSave, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(item.config_value);
  const isBool = item.config_value === 'true' || item.config_value === 'false';
  const isNum  = !isBool && !isNaN(Number(item.config_value)) && item.config_value.trim() !== '';

  const handleSave   = async () => { await onSave(item.id, draft); setEditing(false); };
  const handleCancel = () => { setDraft(item.config_value); setEditing(false); };
  const handleToggle = () => onSave(item.id, item.config_value === 'true' ? 'false' : 'true');

  return (
    <motion.div layout className="bg-white border border-brand-dark rounded-2xl p-5 hover:border-accent-gold/30 transition-all">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="text-sm font-bold text-accent-navy">{item.label}</p>
            {isNum  && <span className="text-[9px] px-2 py-0.5 bg-accent-navy/5 border border-accent-navy/15 text-accent-navy rounded-full font-black uppercase tracking-widest">Numeric</span>}
            {isBool && <span className="text-[9px] px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full font-black uppercase tracking-widest">Toggle</span>}
            {!isBool && !isNum && <span className="text-[9px] px-2 py-0.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-full font-black uppercase tracking-widest">Text</span>}
          </div>
          <p className="text-[11px] text-text-muted leading-relaxed">{item.description}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isBool ? (
            <button onClick={handleToggle} disabled={saving}
              className={`w-12 h-6 rounded-full relative transition-colors ${item.config_value === 'true' ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${item.config_value === 'true' ? 'right-1' : 'left-1'}`} />
            </button>
          ) : editing ? (
            <div className="flex items-center gap-2">
              {isNum ? (
                <input type="number" value={draft} min={0} step={0.01} onChange={e => setDraft(e.target.value)}
                  className="w-24 text-center px-3 py-2 border-2 border-accent-gold rounded-xl text-sm font-bold outline-none text-accent-navy" />
              ) : (
                <textarea value={draft} rows={3} onChange={e => setDraft(e.target.value)}
                  className="w-64 px-3 py-2 border-2 border-accent-gold rounded-xl text-xs font-medium outline-none text-accent-navy resize-none" />
              )}
              <button onClick={handleSave} disabled={saving} className="p-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors">
                {saving ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />}
              </button>
              <button onClick={handleCancel} className="p-2 bg-gray-200 text-gray-600 rounded-xl hover:bg-gray-300 transition-colors">
                <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className={`px-4 py-2 rounded-xl border-2 border-accent-gold/30 bg-accent-gold/5 font-black text-accent-navy ${isNum ? 'min-w-[60px] text-center text-lg' : 'max-w-[200px] text-xs truncate'}`}>
                {item.config_value}
              </div>
              <button onClick={() => { setDraft(item.config_value); setEditing(true); }}
                className="p-2 border border-brand-dark rounded-xl hover:border-accent-gold hover:text-accent-gold transition-all text-text-muted">
                <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3">
        <span className="text-[9px] font-mono text-text-muted bg-brand-light px-2 py-0.5 rounded-lg border border-brand-dark">{item.config_key}</span>
      </div>
    </motion.div>
  );
};

const MonetizationTab = () => {
  const [configs, setConfigs]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [savingId, setSavingId]             = useState(null);
  const [toast, setToast]                   = useState(null);
  const [activeCategory, setActiveCategory] = useState('donations');

  const [plans, setPlans]           = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await writeFetch(`read/${TABLE}?limit=200`);
      const data = await res.json();
      setConfigs(Array.isArray(data.data) ? data.data : []);
    } catch { setConfigs([]); }
    setLoading(false);
  }, []);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const result = await fetchActivePlans();
      // ★ Filter out Will Plans
      const docuVaultPlans = (Array.isArray(result) ? result : []).filter(p => !p.plan_key.startsWith('will_'));
      setPlans(docuVaultPlans);
    } catch (err) {
      setPlansError(err?.message || 'Failed to load plans');
      setPlans([]);
    }
    setPlansLoading(false);
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchPlans(); 
  }, [fetchConfigs, fetchPlans]);

  const handleSave = async (id, value) => {
    setSavingId(id);
    try {
      const res  = await writeFetch(`update/${TABLE}/${id}`, { method: 'PUT', body: JSON.stringify({ config_value: String(value) }) });
      const data = await res.json();
      if (data.status === 'success' || data.ok) {
        setConfigs(prev => prev.map(c => c.id === id ? { ...c, config_value: String(value) } : c));
        showToast('Setting saved successfully');
      } else { showToast('Failed to save setting', 'error'); }
    } catch { showToast('Network error', 'error'); }
    setSavingId(null);
  };

  const grouped = configs.reduce((acc, item) => {
    const cat = item.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const activeMeta  = CATEGORIES.find(c => c.id === activeCategory) || CATEGORIES[0];
  const isStorageTab = activeCategory === 'docuvault';
  const activeItems  = isStorageTab ? [] : (grouped[activeCategory] || []);
  const storageCount = plans.length;

  const freePlan     = plans.find(p => p.price_usd === 0);
  const freeStorageDisplay = freePlan ? formatStorage(freePlan.storage_mb) : '—';

  const getVal = (key) => configs.find(c => c.config_key === key)?.config_value || '—';

  const handleRefreshAll = () => { fetchConfigs(); fetchPlans(); };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-accent-navy via-[#1a2a5e] to-accent-navy text-white rounded-[32px] p-8 flex items-center gap-6 border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiDollarSign} className="w-8 h-8" />
        </div>
        <div className="z-10 flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h2 className="font-serif text-2xl">Monetization Controls</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin</span>
          </div>
          <p className="text-white/60 text-sm font-light max-w-2xl">
            Configure pricing, donation commissions, feature limits, and free-tier usage limits across the entire platform. Storage limits are driven by the Subscription Plans API.
          </p>
        </div>
        <button onClick={handleRefreshAll} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all z-10 flex-shrink-0">
          <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 text-white ${(loading || plansLoading) ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Donation Fee',   value: `${getVal('donation_platform_fee_pct')}%`,    icon: FiHeart,     color: 'text-pink-600',   bg: 'bg-pink-50'   },
          {
            label: 'Free Storage',
            value: plansLoading ? '…' : freeStorageDisplay,
            icon: FiHardDrive,
            color: 'text-indigo-600',
            bg: 'bg-indigo-50',
            badge: 'Plans API',
          },
          { label: 'Free AI Calls',  value: `${getVal('ai_free_requests_per_month')}/mo`, icon: FiCpu,       color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Featured Limit', value: `${getVal('donation_featured_campaign_limit')} camps`, icon: FiZap, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-[24px] border border-brand-dark p-5 text-center relative`}>
            {s.badge && (
              <span className="absolute top-2 right-2 text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-indigo-100 text-indigo-600 border border-indigo-200 rounded-full">
                {s.badge}
              </span>
            )}
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center bg-white border border-brand-dark">
              <SafeIcon icon={s.icon} className={`w-5 h-5 ${s.color}`} />
            </div>
            <p className={`text-xl font-serif font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-start gap-4 p-5 bg-amber-50 border border-amber-200 rounded-2xl">
        <SafeIcon icon={FiInfo} className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-800 mb-1">How Limits Are Applied</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            The <strong>Donation Platform Fee</strong> is deducted automatically from each donation before the net amount is credited to the campaign wallet.
            <strong> Donor Tip</strong> is optional and goes directly to the platform. <strong>Featured Campaign Limit</strong> controls how many campaigns appear on the home page.
            <strong> Storage limits</strong> are read directly from the active Subscription Plans — edit them in Pricing Settings.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-[32px] border border-brand-dark overflow-hidden shadow-sm">
        <div className="flex flex-col md:flex-row min-h-[500px]">
          <div className="md:w-56 bg-brand-light border-b md:border-b-0 md:border-r border-brand-dark flex-shrink-0">
            <div className="p-4">
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-3 px-2">Feature Areas</p>
              <div className="space-y-1">
                {CATEGORIES.map(cat => {
                  const isStorage = cat.id === 'docuvault';
                  const count     = isStorage ? storageCount : (grouped[cat.id] || []).length;
                  const isActive  = activeCategory === cat.id;
                  return (
                    <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${isActive ? 'bg-accent-navy text-white shadow-md' : 'hover:bg-white text-text-muted hover:text-accent-navy'}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-white/20' : cat.bg}`}>
                        <SafeIcon icon={cat.icon} className={`w-3.5 h-3.5 ${isActive ? 'text-white' : cat.color}`} />
                      </div>
                      <span className="text-[11px] font-bold flex-1 truncate">{cat.label}</span>
                      <div className="flex items-center gap-1">
                        {isStorage && (
                          <span className={`text-[7px] font-black px-1 py-0.5 rounded ${isActive ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-500'}`}>API</span>
                        )}
                        {count > 0 && (
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-brand-dark text-text-muted'}`}>{count}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex-1 p-6">
            <div className={`flex items-center gap-4 p-4 rounded-2xl border ${activeMeta.bg} ${activeMeta.border} mb-6`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-white border ${activeMeta.border}`}>
                <SafeIcon icon={activeMeta.icon} className={`w-5 h-5 ${activeMeta.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[11px] font-black uppercase tracking-widest ${activeMeta.color}`}>{activeMeta.label}</p>
                <p className="text-[11px] text-text-muted">{activeMeta.desc}</p>
              </div>
              <div className="flex items-center gap-2">
                {isStorageTab && (
                  <span className="text-[9px] px-2 py-1 bg-indigo-100 text-indigo-600 border border-indigo-200 rounded-full font-black uppercase tracking-widest">
                    Live API
                  </span>
                )}
                <span className={`text-[9px] px-3 py-1 rounded-full font-black border ${activeMeta.bg} ${activeMeta.color} ${activeMeta.border}`}>
                  {isStorageTab ? `${storageCount} plan${storageCount !== 1 ? 's' : ''}` : `${activeItems.length} setting${activeItems.length !== 1 ? 's' : ''}`}
                </span>
              </div>
            </div>

            {isStorageTab ? (
              <StoragePanel
                plans={plans}
                loading={plansLoading}
                error={plansError}
                onRefresh={fetchPlans}
              />
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="w-10 h-10 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading…</p>
              </div>
            ) : activeItems.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiSettings} className="w-8 h-8 text-text-muted/40" />
                </div>
                <p className="text-text-muted text-sm">No settings configured for this category yet.</p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={activeCategory} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                  {activeItems.map(item => (
                    <ConfigRow key={item.id} item={item} onSave={handleSave} saving={savingId === item.id} />
                  ))}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-2 border-dashed border-accent-gold/40 rounded-[32px] p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent-gold/10 border border-accent-gold/30 rounded-2xl flex items-center justify-center">
            <SafeIcon icon={FiZap} className="w-5 h-5 text-accent-gold" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Upgrade Gate Preview</p>
            <p className="text-[10px] text-text-muted">This is what free users see when they hit a limit.</p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-accent-navy to-[#1a2a5e] rounded-2xl p-6 text-white text-center max-w-sm mx-auto">
          <div className="w-12 h-12 bg-accent-gold/20 border border-accent-gold/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLock} className="w-6 h-6 text-accent-gold" />
          </div>
          <p className="font-serif text-lg mb-2">Free Limit Reached</p>
          <p className="text-white/70 text-xs leading-relaxed mb-4">
            {getVal('upgrade_prompt_message') !== '—'
              ? getVal('upgrade_prompt_message')
              : 'You have reached your free plan limit. Upgrade to unlock unlimited access.'}
          </p>
          <button className="px-6 py-2.5 bg-accent-gold text-accent-navy rounded-xl text-[11px] font-black uppercase tracking-widest">
            Upgrade Now
          </button>
        </div>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 20, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 20, x: '-50%' }}
            className={`fixed bottom-8 left-1/2 z-[9999] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl text-white text-sm font-bold ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
            <SafeIcon icon={toast.type === 'error' ? FiAlertTriangle : FiCheck} className="w-4 h-4" />
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MonetizationTab;